import React from 'react';
import { Card } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { LabsmartsGuage } from '../charts/LabsmartsGauge';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  dehydrationGauge,
  DehydrationGauge,
  DehydrationMarker,
} from '../../models/DehydrationGauge';
import { devOnly } from '../../utils/featureVisibility';

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

// Same arrow / list styling as GlucoseRegulationInsights so the two panels read
// as one design.
const arrowStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px',
};

const conditionTitleStyle: React.CSSProperties = {
  color: 'black',
  textAlign: 'left',
};

const markerStyle: React.CSSProperties = {
  color: 'black',
  textAlign: 'right',
};

/**
 * Whether the Electrolytes hydration panel RENDERS in this build.
 *
 * devOnly() rather than visibleExceptProd(): the tier percentages in
 * DehydrationGauge.ts are still pending clinical review, so the panel is held
 * off slava as well as prod (2026-08-28). Widen it back to visibleExceptProd()
 * once the content is ready for the user to review on slava.
 *
 * This says nothing about who owns the dehydration SENTENCE — see
 * HYDRATION_PANEL_OWNS_DEHYDRATION_TEXT below, which is a separate question.
 */
export const DEHYDRATION_PANEL_ENABLED = devOnly();

/**
 * Whether this panel has TAKEN OVER the dehydration statement from the RBC
 * panel's Anemia Status card.
 *
 * Deliberately NOT DEHYDRATION_PANEL_ENABLED, which the handover used to read.
 * That flag only says whether the panel is on a screen, and the handover belongs
 * to the panel's DEPLOY: while this is false the RBC panel keeps the label, so
 * the statement cannot go missing from any report — including the local-dev copy
 * a change is checked against — before the new panel is out there carrying it.
 *
 * The cost while this is false and the panel does render (local dev only) is
 * that the statement appears twice, once under each panel. That is the intended
 * trade: saying it twice in dev is obvious and recoverable, dropping it silently
 * is neither.
 *
 * TO HAND OVER, once the Hydration panel ships: change this to
 * DEHYDRATION_PANEL_ENABLED.
 */
export const HYDRATION_PANEL_OWNS_DEHYDRATION_TEXT = false;

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
};

export class DehydrationInsights extends React.Component<Props, {}> {
  // Whether this panel will render anything, so the parent can drop the eval
  // Col entirely and let the Results panel left-align (same contract as
  // GlucoseRegulationInsights.shouldRender).
  static shouldRender(ba: BloodworkAnalysis): boolean {
    if (!ba?.bloodwork) return false;
    return dehydrationGauge(ba) !== null;
  }

  /**
   * Serializable view of this panel for one test — the gauge, the sentence
   * under it and the hemoconcentration pattern row — mirroring render()
   * exactly. Null when the panel would not render.
   *
   * NOT CALLED by the raw-data export as of 2026-09-02: this panel is on hold,
   * and held work stays out of the export in every build, including the ones
   * that still render it (see AIExportBuilder.ts). Kept ready for the day it
   * ships — the export needs one call and a schema entry in both prompt files.
   */
  static exportInsights(ba: BloodworkAnalysis): {
    gauge: {
      label: string;
      type: string;
      category: string;
      probability: number;
      markers: DehydrationMarker[];
    };
    text?: string;
  } | null {
    if (!ba?.bloodwork) return null;
    const gauge = dehydrationGauge(ba);
    if (!gauge) return null;
    const text = ba.isAnyDehydrationPossibleOrHigher?.()
      ? ba.deriveDehydrationText()
      : undefined;
    return {
      gauge: {
        label: gauge.text,
        type: gauge.type,
        category: gauge.category,
        probability: gauge.probability,
        markers: gauge.markers,
      },
      ...(text ? { text } : {}),
    };
  }

  // ── PDF pagination ──────────────────────────────────────────────────────────
  //
  // Same self-estimating approach as Glucose / Nutrients / Adrenal: the packer
  // needs a height before render, and this panel grows with the number of
  // driver markers the gauge cites.
  static readonly PDF_GAUGE_HEIGHT = 300;
  static readonly PDF_MARKER_ROW_HEIGHT = 18;

  static estimatedPdfHeight(ba: BloodworkAnalysis): number {
    if (!DehydrationInsights.shouldRender(ba)) return 0;
    const gauge = dehydrationGauge(ba);
    const markers = gauge?.markers?.length ?? 0;
    return (
      DehydrationInsights.PDF_GAUGE_HEIGHT +
      markers * DehydrationInsights.PDF_MARKER_ROW_HEIGHT
    );
  }

  renderArrow(marker: DehydrationMarker) {
    const src = marker.direction === 'up' ? arrow_up : arrow_down;
    const alt = marker.direction === 'up' ? '↑' : '↓';
    return (
      <span
        style={{ whiteSpace: 'nowrap' }}
        key={`${marker.direction}-${marker.label}`}
      >
        <img src={src} alt={alt} style={arrowStyle} />
        {marker.label}
      </span>
    );
  }

  renderPattern(gauge: DehydrationGauge) {
    if (!gauge.markers.length) return null;
    const title =
      gauge.type === 'chronic'
        ? 'Chronic pattern — hemoconcentration with raised protein / BUN'
        : gauge.type === 'acute'
        ? 'Acute pattern — hemoconcentration with raised sodium'
        : 'Hemoconcentration pattern';
    return (
      <div className="condition-section">
        <div style={conditionTitleStyle}>{title}</div>
        <div style={markerStyle}>
          {gauge.markers.map(marker => this.renderArrow(marker))}
        </div>
      </div>
    );
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    const gauge = dehydrationGauge(bloodworkAnalysis);
    if (!gauge || !bloodworkAnalysis?.bloodwork) return null;

    return (
      <Card className="evaluationPanel" id="dehydration-insights">
        <Card.Header className="evaluationPanelHeading">
          Hydration Insights
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            <LabsmartsGuage
              text={gauge.text}
              chartContainerId="dehydrationGuage"
              bloodwork={bloodworkAnalysis.bloodwork}
              probability={gauge.probability}
              category={gauge.category}
              ticks={[0, 50, 70, 90]}
            />
            {/*
              Moved here from the RBC Insights panel. Provisional placement
              until the design settles: the gauge's own label already reads
              "<variant> Dehydration is <likelihood> (n%)", so this line
              restates it. Kept verbatim (same derive call, same
              .condition-section styling) so nothing about the wording changes
              in the move.
            */}
            {bloodworkAnalysis.isAnyDehydrationPossibleOrHigher() ? (
              <div className="condition-section">
                {bloodworkAnalysis.deriveDehydrationText()}
              </div>
            ) : null}
            {this.renderPattern(gauge)}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
